/**
 * Onboarding wizard — the site-standup chain as a guided flow, one API call committed per step so a
 * drop-out never loses completed work (the site/gateway already exist and reappear in the list):
 *   1. Site details      → POST /sites
 *   2. Register gateway   → POST /sites/:id/gateways
 *   3. Attach controller  → POST /gateways/:id/controllers
 *   4. Issue certificate  → POST /gateways/:id/cert   (bundle shown once)
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth';
import { ApiError, type CertBundle, type EnrolToken, type ProfileSummary } from '../api';
import { Banner, Button, EnrolTokenCard } from '../ui';

const STEPS = ['Site', 'Gateway', 'Controller', 'Certificate'] as const;

export function NewSiteWizard(): JSX.Element {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // accumulated ids across steps
  const [siteId, setSiteId] = useState('');
  const [gatewayId, setGatewayId] = useState('');
  const [bundle, setBundle] = useState<CertBundle | null>(null);
  const [enrol, setEnrol] = useState<EnrolToken | null>(null);

  // step 1 — site
  const [name, setName] = useState('');
  const [siteCode, setSiteCode] = useState('');
  const [region, setRegion] = useState('');
  const [criticality, setCriticality] = useState<'low' | 'standard' | 'high'>('standard');
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');
  // step 2 — gateway
  const [serial, setSerial] = useState('');
  // step 3 — controller
  const [profiles, setProfiles] = useState<ProfileSummary[]>([]);
  const [profileId, setProfileId] = useState('');
  const [displayName, setDisplayName] = useState('');

  useEffect(() => {
    api
      .listProfiles()
      .then((r) => {
        setProfiles(r.profiles);
        if (r.profiles[0]) setProfileId(r.profiles[0].id);
      })
      .catch(() => setProfiles([]));
  }, [api]);

  function fail(e: unknown, fallback: string) {
    setErr(e instanceof ApiError ? e.message : fallback);
  }

  async function saveSite(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const latN = Number(lat);
      const lonN = Number(lon);
      const r = await api.createSite({
        name: name.trim(),
        site_code: siteCode.trim(),
        ...(region.trim() ? { region: region.trim() } : {}),
        criticality,
        ...(lat.trim() && !Number.isNaN(latN) ? { lat: latN } : {}),
        ...(lon.trim() && !Number.isNaN(lonN) ? { lon: lonN } : {}),
      });
      setSiteId(r.id);
      setStep(1);
    } catch (e) {
      fail(e, 'Could not create the site.');
    } finally {
      setBusy(false);
    }
  }

  async function saveGateway(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const r = await api.createGateway(siteId, serial.trim());
      setGatewayId(r.id);
      setStep(2);
    } catch (e) {
      fail(e, 'Could not register the gateway.');
    } finally {
      setBusy(false);
    }
  }

  async function saveController(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.createController(gatewayId, {
        profile_id: profileId,
        ...(displayName.trim() ? { display_name: displayName.trim() } : {}),
      });
      setStep(3);
    } catch (e) {
      fail(e, 'Could not attach the controller.');
    } finally {
      setBusy(false);
    }
  }

  async function issueCert() {
    setBusy(true);
    setErr(null);
    try {
      setBundle(await api.issueCert(gatewayId));
    } catch (e) {
      if (e instanceof ApiError && e.status === 503)
        setErr(
          'The site is provisioned, but no certificate authority is configured on the server, so the gateway cert cannot be issued yet. You can issue it later from the site page.',
        );
      else fail(e, 'Could not issue the certificate.');
    } finally {
      setBusy(false);
    }
  }

  async function mintEnrolToken() {
    setBusy(true);
    setErr(null);
    try {
      setEnrol(await api.issueEnrolToken(gatewayId));
    } catch (e) {
      fail(e, 'Could not mint the enrolment token.');
    } finally {
      setBusy(false);
    }
  }

  function downloadBundle() {
    if (!bundle) return;
    const payload = {
      gateway_id: bundle.gateway_id,
      fingerprint: bundle.fingerprint,
      not_after: bundle.not_after,
      ...bundle.bundle,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gateway-${bundle.gateway_id}-enrolment.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Onboard a site</h1>
          <div className="lead">
            Stand up a new site: create it, register its gateway, attach the controller, enrol it.
          </div>
        </div>
      </div>

      <div className="steps" aria-label="progress">
        {STEPS.map((label, i) => (
          <div className={`s ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`} key={label}>
            <span className="n">{i < step ? '✓' : i + 1}</span>
            <span>{label}</span>
            {i < STEPS.length - 1 && <span className="bar" />}
          </div>
        ))}
      </div>

      {err && <Banner kind="err">{err}</Banner>}

      <div className="panel panel-pad">
        {step === 0 && (
          <form onSubmit={saveSite}>
            <div className="field">
              <label htmlFor="name">Site name</label>
              <input
                id="name"
                className="input"
                placeholder="Mount Tamborine Tower"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="field row">
              <div>
                <label htmlFor="code">
                  Site code <span className="opt">(unique)</span>
                </label>
                <input
                  id="code"
                  className="input mono"
                  placeholder="QLD-142"
                  value={siteCode}
                  onChange={(e) => setSiteCode(e.target.value)}
                  required
                />
              </div>
              <div>
                <label htmlFor="region">
                  Region <span className="opt">(optional)</span>
                </label>
                <input
                  id="region"
                  className="input"
                  placeholder="QLD"
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                />
              </div>
            </div>
            <div className="field">
              <label htmlFor="crit">Criticality</label>
              <select
                id="crit"
                className="select"
                value={criticality}
                onChange={(e) => setCriticality(e.target.value as typeof criticality)}
              >
                <option value="low">Low</option>
                <option value="standard">Standard</option>
                <option value="high">High</option>
              </select>
            </div>
            <div className="field row">
              <div>
                <label htmlFor="lat">
                  Latitude <span className="opt">(optional)</span>
                </label>
                <input
                  id="lat"
                  className="input mono"
                  inputMode="decimal"
                  placeholder="-24.87"
                  value={lat}
                  onChange={(e) => setLat(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="lon">
                  Longitude <span className="opt">(optional)</span>
                </label>
                <input
                  id="lon"
                  className="input mono"
                  inputMode="decimal"
                  placeholder="152.35"
                  value={lon}
                  onChange={(e) => setLon(e.target.value)}
                />
              </div>
            </div>
            <div className="hint" style={{ marginTop: -6, marginBottom: 14 }}>
              Coordinates place the site on the fleet map. Leave blank to add them later.
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-ghost" onClick={() => navigate('/sites')}>
                Cancel
              </button>
              <Button type="submit" busy={busy}>
                Create site →
              </Button>
            </div>
          </form>
        )}

        {step === 1 && (
          <form onSubmit={saveGateway}>
            <p className="muted" style={{ marginTop: 0 }}>
              Register the RUT956 router that backhauls this site.
            </p>
            <div className="field">
              <label htmlFor="gw">RUT956 serial</label>
              <input
                id="gw"
                className="input mono"
                placeholder="RUT956-XXXXXXXX"
                value={serial}
                onChange={(e) => setSerial(e.target.value)}
                required
                autoFocus
              />
              <div className="hint">
                Printed on the router label; must be unique in this workspace.
              </div>
            </div>
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => navigate(`/sites/${siteId}`)}
              >
                Finish later
              </button>
              <Button type="submit" busy={busy}>
                Register gateway →
              </Button>
            </div>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={saveController}>
            <p className="muted" style={{ marginTop: 0 }}>
              Attach the generator controller behind this gateway.
            </p>
            <div className="field">
              <label htmlFor="profile">Controller profile</label>
              <select
                id="profile"
                className="select"
                value={profileId}
                onChange={(e) => setProfileId(e.target.value)}
              >
                {profiles.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <div className="hint">
                The register map the edge agent uses to read this controller.
              </div>
            </div>
            <div className="field">
              <label htmlFor="dn">
                Display name <span className="opt">(optional)</span>
              </label>
              <input
                id="dn"
                className="input"
                placeholder="Genset 1"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </div>
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => navigate(`/sites/${siteId}`)}
              >
                Finish later
              </button>
              <Button type="submit" busy={busy} disabled={!profileId}>
                Attach controller →
              </Button>
            </div>
          </form>
        )}

        {step === 3 && (
          <div>
            {!bundle ? (
              <>
                <p className="muted" style={{ marginTop: 0 }}>
                  Last step: issue the gateway’s mTLS certificate. The bundle — including its
                  private key — is shown once, to install on the device.
                </p>
                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => navigate(`/sites/${siteId}`)}
                  >
                    Skip for now
                  </button>
                  <Button busy={busy} onClick={issueCert}>
                    Issue certificate
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Banner kind="ok">
                  Site onboarded. The certificate is held by the platform for the cloud edge; the
                  router now enrols itself with a token.
                </Banner>
                <dl className="kv">
                  <dt>Fingerprint</dt>
                  <dd className="mono">{bundle.fingerprint}</dd>
                  <dt>Expires</dt>
                  <dd>{new Date(bundle.not_after).toLocaleString()}</dd>
                </dl>
                {enrol ? (
                  <EnrolTokenCard token={enrol} onDismiss={() => setEnrol(null)} />
                ) : (
                  <p className="muted">
                    Next: mint the router’s enrolment token and run the printed line on the RUT956.
                    The bundle download is only for a site that will run its own edge.
                  </p>
                )}
                <div className="form-actions">
                  <button className="btn btn-ghost" onClick={() => navigate(`/sites/${siteId}`)}>
                    Go to site
                  </button>
                  <button className="btn btn-ghost" onClick={downloadBundle}>
                    Download bundle
                  </button>
                  {!enrol && (
                    <Button busy={busy} onClick={mintEnrolToken}>
                      Mint router token
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}
